/**
 * React Query client + localStorage persistence.
 *
 * Persistence is OPT-IN per query: only queries created with
 * `meta: { persist: true }` are written to localStorage. Keep the whitelist
 * to small, slow-changing payloads on the critical render path (tank
 * parameters/features, tanks list, notification preferences, reference
 * data). Never mark bulk chart/sensor data — localStorage has a ~5MB budget
 * and large writes fail silently.
 *
 * On page load, persisted entries hydrate synchronously (nav renders from
 * last-known feature flags), then React Query refetches anything past its
 * staleTime in the background (stale-while-revalidate).
 */
import { QueryClient, type Query } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import type {
  PersistedClient,
  PersistQueryClientOptions,
} from '@tanstack/react-query-persist-client';
import { CACHE_TIMES } from '@/config/cacheTimes';

// Typed `meta` so `meta: { persist: true }` is checked by the compiler.
declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { persist?: boolean };
  }
}

export const PERSIST_STORAGE_KEY = 'rdh-query-cache';

// Bump to discard all persisted caches on deploy of a breaking shape change
// (e.g. an API response field renamed). Hydration drops non-matching busters.
export const PERSIST_BUSTER = 'v1';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: CACHE_TIMES.DATA,
      // Must be >= PERSIST_MAX_AGE, or inactive queries are GC'd from memory
      // before the persister can write them.
      gcTime: CACHE_TIMES.PERSIST_MAX_AGE,
    },
  },
});

function getSafeStorage(): Storage | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    // SecurityError (site data blocked) — run without persistence
    return undefined;
  }
}

/**
 * Query roots never written to localStorage, whatever their meta says: tank
 * shots and organism photos hold signed photo URLs, share links hold their
 * tokens (bearer links to the owner's photos, design 8.6).
 */
export const NEVER_PERSISTED_ROOTS: readonly string[] = [
  'tank-photos',
  'livestock-photos',
  'share-links',
];

function neverPersisted(queryKey: readonly unknown[] | undefined): boolean {
  const root = queryKey?.[0];
  return typeof root === 'string' && NEVER_PERSISTED_ROOTS.includes(root);
}

/** Signed photo URLs on a tank (tank shots, docs/plans/2026-10-28-tank-shots-implementation.md 3.4). */
const TANK_SIGNED_URL_FIELDS = ['cover_thumb_url', 'cover_display_url'] as const;

/**
 * Signed URLs are bearer links to private photos and expire: they never go to
 * localStorage. The tanks list is persisted without its cover URLs and marked
 * stale (dataUpdatedAt 0), so after a reload it is refetched for fresh ones.
 * Tank shot, organism photo and share link queries are never persisted, whatever their meta says.
 */
export function stripSignedUrls(client: PersistedClient): PersistedClient {
  const queries = client.clientState.queries
    .filter((query) => !neverPersisted(query.queryKey))
    .map((query) => {
      if (query.queryKey[0] !== 'tanks' || !Array.isArray(query.state.data)) return query;
      let stripped = false;
      const data = (query.state.data as Record<string, unknown>[]).map((tank) => {
        if (!TANK_SIGNED_URL_FIELDS.some((field) => tank[field])) return tank;
        stripped = true;
        const copy = { ...tank };
        for (const field of TANK_SIGNED_URL_FIELDS) copy[field] = null;
        return copy;
      });
      if (!stripped) return query;
      return { ...query, state: { ...query.state, data, dataUpdatedAt: 0 } };
    });
  return { ...client, clientState: { ...client.clientState, queries } };
}

export const persister = createSyncStoragePersister({
  storage: getSafeStorage(),
  key: PERSIST_STORAGE_KEY,
  serialize: (client) => JSON.stringify(stripSignedUrls(client)),
});

export function shouldDehydrateQuery(query: Query): boolean {
  return (
    query.state.status === 'success' &&
    query.meta?.persist === true &&
    !neverPersisted(query.queryKey)
  );
}

export const persistOptions: Omit<PersistQueryClientOptions, 'queryClient'> = {
  persister,
  maxAge: CACHE_TIMES.PERSIST_MAX_AGE,
  buster: PERSIST_BUSTER,
  dehydrateOptions: {
    shouldDehydrateQuery,
    // Persistence is queries-only: without this, the library default writes
    // PAUSED mutations (e.g. fired while offline) to localStorage.
    shouldDehydrateMutation: () => false,
  },
};

/** Full cache wipe: in-memory query cache + persisted localStorage entry. */
export function clearPersistedCache(): void {
  queryClient.clear();
  try {
    persister.removeClient();
  } catch {
    // localStorage unavailable (private mode) — in-memory clear is enough
  }
}
